import type { SkinAnalysisDemoAnswer, SkinAnalysisDemoAnswerValue } from '../../infrastructure/SkinAnalysisDemoClient';
import type {
  Questionnaire,
  QuestionnaireClient,
  QuestionnaireQuestion,
  QuestionnaireSchema,
} from '../../infrastructure/QuestionnaireClient';

export type SkinMatchQuestionType = 'single' | 'multi' | 'ranked_multi';
export type SkinMatchQuestionLayout = 'list' | 'grid';
export type SkinMatchSurveyAnswers = Record<string, SkinAnalysisDemoAnswerValue | undefined>;

export interface SkinMatchSurveyOption {
  value: string;
  label: string;
  description?: string;
  clearOthers?: boolean;
}

export interface SkinMatchSurveyQuestion {
  id: string;
  label: string;
  prompt: string;
  helper?: string;
  type: SkinMatchQuestionType;
  layout?: SkinMatchQuestionLayout;
  allowSelectAll?: boolean;
  options: SkinMatchSurveyOption[];
}

export interface BuildSkinMatchDemoAnswersRequest {
  quizAnswers: SkinMatchSurveyAnswers;
  hairType: string;
  mainHairConcern: string;
  hasColoredHair: boolean;
  hasHeatStyling: boolean;
}

export const SKIN_MATCH_DEMO_SURVEY_UUID = 'skin-match-demo-12q-v1';
export const SKIN_MATCH_DEMO_SURVEY_TITLE = 'Skin Match Demo Survey 12Q v1';
export const SKIN_MATCH_DEMO_RESPONSE_SOURCE = 'admin_skin_analysis_demo';

export const skinMatchSurveyQuestions: SkinMatchSurveyQuestion[] = [
  {
    id: 'age',
    label: 'How old are you?',
    prompt: 'How old are you?',
    helper: 'At different ages, your skin needs different care.',
    type: 'single',
    options: [
      { value: 'under-25', label: 'Under 25' },
      { value: '25-34', label: '25 – 34' },
      { value: '35-44', label: '35 – 44' },
      { value: '45-60', label: '45 – 60' },
      { value: 'over-60', label: 'Over 60' },
    ],
  },
  {
    id: 'gender',
    label: 'Gender',
    prompt: 'What gender do you identify with?',
    helper: 'Hormones can shape how skin looks and feels at every age.',
    type: 'single',
    options: [
      { value: 'female', label: 'Female' },
      { value: 'male', label: 'Male' },
      { value: 'non-binary', label: 'Non-binary' },
    ],
  },
  {
    id: 'sensitive',
    label: 'Skin sensitivity',
    prompt: 'Would you consider your skin sensitive?',
    type: 'single',
    options: [
      {
        value: 'sensitive',
        label: 'Sensitive',
        description: 'My skin has reacted negatively to some skincare in the past.',
      },
      {
        value: 'non-sensitive',
        label: 'Non-sensitive',
        description: 'My skin shows no reaction to certain ingredients.',
      },
    ],
  },
  {
    id: 'skintype',
    label: 'Skin type',
    prompt: 'What would you say your skin type is?',
    type: 'single',
    options: [
      { value: 'oily', label: 'Oily', description: 'Excess sebum, shiny, enlarged pores.' },
      { value: 'dry', label: 'Dry', description: 'Lacks moisture, feels tight, flakiness.' },
      { value: 'normal', label: 'Normal', description: 'Neither oily nor dry, smooth texture.' },
      { value: 'combination', label: 'Combination', description: 'T-zone oiliness, visible pores.' },
    ],
  },
  {
    id: 'goal',
    label: 'Main skincare goal',
    prompt: 'Pick the main skincare goal',
    helper: 'Focus on one visible result first.',
    type: 'single',
    options: [
      { value: 'lines', label: 'Wrinkles and Fine Lines' },
      { value: 'breakouts', label: 'Acne, Blemishes, and Breakouts' },
      { value: 'pores', label: 'Visible Pores' },
      { value: 'pigmentation', label: 'Pigmentation' },
      { value: 'post-acne-spots', label: 'Post-acne Spots' },
      { value: 'uneven-texture', label: 'Uneven Skin Texture' },
      { value: 'dryness', label: 'Dry or Flaky Skin' },
      { value: 'redness', label: 'Redness and Irritation' },
      { value: 'oiliness', label: 'Oily Shine' },
    ],
  },
  {
    id: 'concerns',
    label: 'Additional skin concerns',
    prompt: 'What are your additional skin concerns?',
    helper: 'Pick as many as you like.',
    type: 'multi',
    layout: 'grid',
    allowSelectAll: true,
    options: [
      { value: 'redness', label: 'Redness or rosacea' },
      { value: 'lines', label: 'Fine lines and wrinkles' },
      { value: 'breakouts', label: 'Pimples or acne' },
      { value: 'pigmentation', label: 'Pigmentation changes' },
      { value: 'pores', label: 'Enlarged pores' },
      { value: 'dullness', label: 'Dullness' },
      { value: 'puffiness', label: 'Puffy eyes' },
      { value: 'dark_circles', label: 'Dark circles' },
      { value: 'dryness', label: 'Dryness' },
      { value: 'oiliness', label: 'Oiliness' },
    ],
  },
  {
    id: 'budget',
    label: 'Skincare item budget',
    prompt: 'How much do you typically spend on a skincare item?',
    helper: 'Your budget for everyday products.',
    type: 'single',
    options: [
      {
        value: 'under-20',
        label: 'Smart Savings ($19 and less)',
        description: 'Reliable performance, everyday affordability.',
      },
      {
        value: '20-49',
        label: 'Balanced Value ($20–$49)',
        description: 'Great balance of efficacy and cost.',
      },
      {
        value: '50-99',
        label: 'Professional ($50–$99)',
        description: 'Advanced technology and ingredients.',
      },
      { value: '100-plus', label: 'Ultimate ($100+)', description: 'Exclusive products for the discerning.' },
    ],
  },
  {
    id: 'factors',
    label: 'Skincare purchase factors',
    prompt: 'Pick the factors that matter when choosing skincare',
    helper: 'These can be used as product matching signals.',
    type: 'multi',
    layout: 'grid',
    allowSelectAll: true,
    options: [
      { value: 'sulfate-free', label: 'Sulfate-free' },
      { value: 'fragrance-free', label: 'Fragrance-free' },
      { value: 'non-comedogenic', label: 'Non-comedogenic' },
      { value: 'alcohol-free', label: 'Alcohol-free' },
      { value: 'paraben-free', label: 'Paraben-free' },
      { value: 'vegan', label: 'Vegan' },
      { value: 'cruelty-free', label: 'Cruelty-free' },
      { value: 'none', label: 'No preferences', clearOthers: true },
    ],
  },
  {
    id: 'allergy',
    label: 'Ingredient reactions',
    prompt: 'Ever had a reaction or allergy to ingredients?',
    helper: 'These answers can become exclusion signals.',
    type: 'multi',
    layout: 'grid',
    options: [
      { value: 'retinoids', label: 'Retinoids / Vit A' },
      { value: 'niacinamide', label: 'Niacinamide / B3' },
      { value: 'vitamin-c', label: 'Vitamin C' },
      { value: 'aha', label: 'AHAs' },
      { value: 'bha', label: 'BHA' },
      { value: 'fragrance', label: 'Fragrance' },
      { value: 'essential-oils', label: 'Essential oils' },
      { value: 'none', label: 'No / Not sure', clearOthers: true },
    ],
  },
  {
    id: 'special',
    label: 'Special skin conditions',
    prompt: 'Any special conditions that might affect your skin?',
    type: 'multi',
    layout: 'grid',
    options: [
      { value: 'pregnancy-breastfeeding', label: 'Pregnancy / Breastfeeding' },
      { value: 'hormonal-imbalance', label: 'Hormonal imbalances' },
      { value: 'rosacea-eczema', label: 'Rosacea / Eczema' },
      { value: 'photosensitive', label: 'Photosensitivity / Sun sensitivity' },
      { value: 'none', label: "No, I don't", clearOthers: true },
    ],
  },
  {
    id: 'lineup',
    label: 'Current skincare lineup',
    prompt: 'Select the products in your current lineup',
    type: 'multi',
    options: [
      { value: 'none', label: 'No current routine, ready to begin!', clearOthers: true },
      { value: 'cleanser', label: 'Cleanser' },
      { value: 'toner', label: 'Toner' },
      { value: 'moisturizer', label: 'Moisturizer' },
      { value: 'face-serum', label: 'Face Serum' },
      { value: 'eye-cream', label: 'Eye cream' },
      { value: 'sunscreen', label: 'SPF' },
      { value: 'exfoliator', label: 'Exfoliator' },
    ],
  },
  {
    id: 'spf',
    label: 'Sunscreen usage',
    prompt: 'Do you wear sunscreen outdoors?',
    type: 'single',
    options: [
      { value: 'always', label: 'Yes, always' },
      { value: 'sunny-only', label: 'Only in sunny weather' },
      { value: 'sometimes', label: 'Sometimes' },
      { value: 'rarely', label: 'Rarely' },
      { value: 'never', label: 'Never' },
    ],
  },
];

const toQuestionnaireQuestion = (question: SkinMatchSurveyQuestion): QuestionnaireQuestion => ({
  questionId: question.id,
  type: question.type,
  label: question.label,
  options: question.options.map((option) => ({
    value: option.value,
    label: option.label,
  })),
});

const skinMainConcernOptions =
  skinMatchSurveyQuestions
    .find((question) => question.id === 'goal')
    ?.options.map((option) => ({ value: option.value, label: option.label })) ?? [];

export const skinMatchQuestionnaireSchema: QuestionnaireSchema = {
  questions: [
    ...skinMatchSurveyQuestions.map(toQuestionnaireQuestion),
    {
      questionId: 'skin-main-concern',
      type: 'single',
      label: 'Main skin concern',
      options: skinMainConcernOptions,
    },
    {
      questionId: 'hair-type',
      type: 'single',
      label: 'Hair type',
      options: ['Straight', 'Wavy', 'Curly', 'Coily'].map((label) => ({ value: label, label })),
    },
    {
      questionId: 'hair-main-concern',
      type: 'single',
      label: 'Main hair concern',
      options: ['Frizz', 'Damage', 'Volume', 'Oiliness', 'Dry scalp'].map((label) => ({ value: label, label })),
    },
    {
      questionId: 'hair-procedures',
      type: 'multi',
      label: 'Hair procedures',
      options: ['Colored hair', 'Heat styling'].map((label) => ({ value: label, label })),
    },
  ],
};

export const buildSkinMatchDemoQuestionnaireRequest = () => ({
  surveyUuid: SKIN_MATCH_DEMO_SURVEY_UUID,
  title: SKIN_MATCH_DEMO_SURVEY_TITLE,
  formType: 'skin_quiz' as const,
  schema: skinMatchQuestionnaireSchema,
  createdBy: 'admin-demo',
});

const isPublishedDemoQuestionnaire = (questionnaire: Questionnaire): boolean =>
  questionnaire.status === 'published' && questionnaire.latestVersion?.status === 'published';

export const ensureSkinMatchDemoQuestionnaire = async (
  client: Pick<QuestionnaireClient, 'listQuestionnaires' | 'createQuestionnaire' | 'publish'>,
): Promise<Questionnaire> => {
  skinMatchDemoQuestionnairePromise ??= loadSkinMatchDemoQuestionnaire(client).finally(() => {
    skinMatchDemoQuestionnairePromise = undefined;
  });
  return skinMatchDemoQuestionnairePromise;
};

let skinMatchDemoQuestionnairePromise: Promise<Questionnaire> | undefined;

const loadSkinMatchDemoQuestionnaire = async (
  client: Pick<QuestionnaireClient, 'listQuestionnaires' | 'createQuestionnaire' | 'publish'>,
): Promise<Questionnaire> => {
  const existing = (await client.listQuestionnaires()).find(
    (questionnaire) => questionnaire.surveyUuid === SKIN_MATCH_DEMO_SURVEY_UUID,
  );
  const request = buildSkinMatchDemoQuestionnaireRequest();

  if (!existing) {
    const created = await client.createQuestionnaire(request);
    return client.publish(created.id);
  }

  if (isPublishedDemoQuestionnaire(existing)) {
    return existing;
  }

  return client.publish(existing.id);
};

const isAnsweredValue = (answer: SkinAnalysisDemoAnswerValue | undefined): answer is SkinAnalysisDemoAnswerValue => {
  if (Array.isArray(answer)) return answer.length > 0;
  return answer !== undefined && answer !== '';
};

export const isQuestionAnswered = (question: SkinMatchSurveyQuestion, answers: SkinMatchSurveyAnswers): boolean =>
  isAnsweredValue(answers[question.id]);

export const getAnswerValues = (answer: SkinAnalysisDemoAnswerValue | undefined): string[] => {
  if (Array.isArray(answer)) return answer;
  if (answer === undefined || answer === '') return [];
  return [String(answer)];
};

export const getSelectableOptionValues = (question: SkinMatchSurveyQuestion): string[] =>
  question.options.filter((option) => !option.clearOthers).map((option) => option.value);

export const areAllSelectableOptionsSelected = (
  question: SkinMatchSurveyQuestion,
  answer: SkinAnalysisDemoAnswerValue | undefined,
): boolean => {
  const selectableValues = getSelectableOptionValues(question);
  const currentValues = getAnswerValues(answer);
  return selectableValues.length > 0 && selectableValues.every((value) => currentValues.includes(value));
};

export const isMultiQuestion = (question: SkinMatchSurveyQuestion): boolean =>
  question.type === 'multi' || question.type === 'ranked_multi';

export const getSelectedRank = (
  question: SkinMatchSurveyQuestion,
  answers: SkinMatchSurveyAnswers,
  optionValue: string,
): number | undefined => {
  if (question.type !== 'ranked_multi') return undefined;

  const answer = answers[question.id];
  if (!Array.isArray(answer)) return undefined;

  const rankIndex = answer.indexOf(optionValue);
  return rankIndex === -1 ? undefined : rankIndex + 1;
};

export const isSelected = (
  question: SkinMatchSurveyQuestion,
  answers: SkinMatchSurveyAnswers,
  optionValue: string,
): boolean => {
  const answer = answers[question.id];
  if (Array.isArray(answer)) return answer.includes(optionValue);
  return answer === optionValue;
};

export const getDisplayLabels = (
  question: SkinMatchSurveyQuestion,
  answer: SkinAnalysisDemoAnswerValue | undefined,
): string[] => {
  const labelsByValue = new Map(question.options.map((option) => [option.value, option.label]));
  return getAnswerValues(answer).map((value) => labelsByValue.get(value) ?? value);
};

export const buildSkinMatchDemoAnswers = ({
  quizAnswers,
  hairType,
  mainHairConcern,
  hasColoredHair,
  hasHeatStyling,
}: BuildSkinMatchDemoAnswersRequest): SkinAnalysisDemoAnswer[] => {
  const answers: SkinAnalysisDemoAnswer[] = [];

  for (const question of skinMatchSurveyQuestions) {
    const answer = quizAnswers[question.id];
    if (isAnsweredValue(answer)) {
      answers.push({
        questionId: question.id,
        label: question.label,
        answer,
      });
    }
  }

  const goal = quizAnswers['goal'];
  if (typeof goal === 'string' && goal) {
    answers.push({
      questionId: 'skin-main-concern',
      label: 'Main skin concern',
      answer: goal,
    });
  }

  answers.push(
    { questionId: 'hair-type', label: 'Hair type', answer: hairType },
    { questionId: 'hair-main-concern', label: 'Main hair concern', answer: mainHairConcern },
    {
      questionId: 'hair-procedures',
      label: 'Hair procedures',
      answer: [...(hasColoredHair ? ['Colored hair'] : []), ...(hasHeatStyling ? ['Heat styling'] : [])],
    },
  );

  return answers;
};
